import { useState } from "react";

import type { CredentialKeyInfo } from "@/api/settings";
import { Button } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDate } from "@/lib/utils";

const TICIMAX_FIELDS = [
  { key: "ticimax_username", labelKey: "kurulum.ticimax.username" as const, type: "text" as const },
  { key: "ticimax_password", labelKey: "kurulum.ticimax.password" as const, type: "password" as const },
  { key: "ticimax_totp_secret", labelKey: "kurulum.ticimax.totp_secret" as const, type: "password" as const },
  { key: "ticimax_ws_key", labelKey: "kurulum.ticimax.ws_key" as const, type: "password" as const },
];

interface TicimaxCredentialsFormProps {
  existing: CredentialKeyInfo[];
  onSave: (values: Record<string, string>) => void;
  isSaving: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Ticimax kimlik bilgisi formu — /kurulum adim 2 (gorev talimati madde 3).
 * DEGERLER ASLA GORUNTULENMEZ (sozlesme §5) — bos birakilan alan HIC
 * GONDERILMEZ (set_credentials bos string'i SILME olarak yorumlar, backend
 * "engeller" tuzagi — trap #1), yalniz DOLDURULAN alanlar PUT edilir.
 */
export function TicimaxCredentialsForm({ existing, onSave, isSaving, error, success }: TicimaxCredentialsFormProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const existingByKey = new Map(existing.map((k) => [k.key, k]));

  function handleSubmit() {
    const touched = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim().length > 0));
    if (Object.keys(touched).length === 0) return;
    onSave(touched);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">{t("kurulum.ticimax.empty_hint")}</p>
      {error && (
        <p role="alert" className="text-sm text-red">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="text-sm text-green">
          {t("kurulum.ticimax.saved")}
        </p>
      )}

      {TICIMAX_FIELDS.map((field) => {
        const info = existingByKey.get(field.key);
        return (
          <label key={field.key} className="block">
            <span className="label">{t(field.labelKey)}</span>
            <input
              type={field.type}
              autoComplete="off"
              value={values[field.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
              placeholder={info ? undefined : t("kurulum.ticimax.not_set")}
              className="input"
            />
            {info && (
              <span className="mt-1 block text-xs text-muted">
                {t("kurulum.ticimax.existing", { masked: info.masked, date: fmtDate(info.updated_at) })}
              </span>
            )}
          </label>
        );
      })}

      <Button type="button" variant="primary" disabled={isSaving} onClick={handleSubmit}>
        {t("kurulum.ticimax.save")}
      </Button>
    </div>
  );
}
